import { Add } from '@mui/icons-material'
import { Menu, MenuItem } from '@mui/material'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'
import React, { RefObject, useEffect, useMemo, useState } from 'react'
import { useAppSelector } from '../../../store/redux/hooks'
import { useGetUsersQuery } from '../../../store/redux/reducers/usersSlice'
import { User } from '../../../store/types'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getCurrentUser, getIsManagement } from '../../../store/redux/reducers/currentUserSlice'
import { ExtraSmallSquareButton } from '../../../styles/ButtonStyles'
import { useGetLeaderAndCoLeadersQuery } from '../../../store/redux/reducers/projectsSlice'

type AddItemPopupProps = {
  handleItemClick: (employee: User) => void
  employeesAdded: User[]
  vacancyRef: React.RefObject<HTMLButtonElement>
  addEmployeeRef: RefObject<HTMLButtonElement>
  isVacancyVisible: boolean
}

const AddEmployeeMenuPopup = ({
  handleItemClick: handleClick,
  employeesAdded,
  vacancyRef,
  addEmployeeRef,
  isVacancyVisible,
}: AddItemPopupProps) => {
  const { t } = useTranslation()
  const { leaderId: projectLeaderId } = useAppSelector(state => state.leaderId)
  const [page, setPage] = useState(0)
  const currentUser = useSelector(getCurrentUser)
  const isManagement = useSelector(getIsManagement)
  const [employees, setEmployees] = useState<User[]>([])

  const { data: projectLeadersEmployeesFetchResult } = useGetUsersQuery({
    page: 0,
    size: 500,
    'projects.leaders.leader.id': projectLeaderId,
  })

  const { data: employeesFetchResult, isSuccess } = useGetUsersQuery({
    page: page,
    size: 10,
  })

  const { data: leadersFetchResult } = useGetLeaderAndCoLeadersQuery({
    id: currentUser?.id ?? '',
  })

  const [allLoadedEmployees, setAllLoadedEmployees] = useState<User[]>([])

  useEffect(() => {
    if (isSuccess && employeesFetchResult?.content) {
      setAllLoadedEmployees(prevEmployees => {
        const newEmployees = employeesFetchResult.content.filter(
          newEmp => !prevEmployees.some(emp => emp.id === newEmp.id)
        )
        return [...prevEmployees, ...newEmployees]
      })
    }
  }, [employeesFetchResult, isSuccess])

  const team = useMemo(() => {
    if (!projectLeadersEmployeesFetchResult || !leadersFetchResult) {
      return []
    }
    let temp: User[] = [...projectLeadersEmployeesFetchResult.content, ...leadersFetchResult]
    return temp.filter(employee => !employeesAdded.some(addedEmployee => addedEmployee.id === employee.id))
  }, [projectLeadersEmployeesFetchResult, employeesAdded, leadersFetchResult])

  useEffect(() => {
    if (isSuccess) {
      let filteredEmployees = allLoadedEmployees.filter(
        employee =>
          !employeesAdded.some(addedEmployee => addedEmployee.id === employee.id) &&
          !team.some(emp => emp.id === employee.id)
      )
      setEmployees(prevEmployees => {
        const filteredPrevEmployees = prevEmployees.filter(
          emp => !employeesAdded.some(ae => ae.id === emp.id) && !team.some(empT => empT.id === emp.id)
        )
        const newUniqueEmployees = filteredEmployees.filter(
          newEmp => !filteredPrevEmployees.some(prevEmp => prevEmp.id === newEmp.id)
        )
        return [...filteredPrevEmployees, ...newUniqueEmployees].sort((a, b) => a.firstName.localeCompare(b.firstName))
      })
    }
  }, [isSuccess, isManagement, projectLeaderId, employeesAdded, currentUser, allLoadedEmployees, team])

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const bottomReached =
      event.currentTarget.scrollHeight - event.currentTarget.scrollTop === event.currentTarget.clientHeight
    if (bottomReached && employeesFetchResult && !employeesFetchResult.last) {
      setPage(prevPage => prevPage + 1)
    }
  }

  return (
    <PopupState variant="popover" popupId="month-menu-popper" disableAutoFocus={false} parentPopupState={null}>
      {popupState => (
        <React.Fragment>
          <ExtraSmallSquareButton
            aria-label={t('page.projectDistribution.aria.addEmployeeMenu')}
            ref={addEmployeeRef}
            color="secondary"
            {...bindTrigger(popupState)}
            onKeyDown={event => {
              if (event.key === 'Tab' && !event.shiftKey && isVacancyVisible) {
                event.preventDefault()
                vacancyRef.current?.focus()
              }
            }}
          >
            <Add />
          </ExtraSmallSquareButton>
          <Menu
            {...bindMenu(popupState)}
            slotProps={{
              paper: {
                elevation: 3,
                sx: {
                  maxHeight: 250,
                  overflowY: 'auto',
                },
                onScroll: handleScroll,
              },
            }}
          >
            {team.length > 0 && (
              <MenuItem
                disabled
                sx={{
                  px: 1,
                  fontWeight: 'bold',
                  pointerEvents: 'none',
                  color: 'text.highEmphasis',
                  '&.Mui-disabled': {
                    opacity: 1,
                  },
                }}
              >
                {t('page.projectDistribution.teamEmployees')}
              </MenuItem>
            )}
            {team.map(employee => (
              <MenuItem
                key={employee.id}
                onClick={() => handleClick(employee)}
                value={employee.id}
                sx={{ color: 'text.base' }}
              >
                {`${employee.firstName} ${employee.lastName}`}
              </MenuItem>
            ))}

            <MenuItem
              disabled
              sx={{
                px: 1,
                fontWeight: 'bold',
                pointerEvents: 'none',
                color: 'text.highEmphasis',
                '&.Mui-disabled': {
                  opacity: 1,
                },
              }}
            >
              {t('page.projectDistribution.otherEmployees')}
            </MenuItem>
            {employees.length > 0 ? (
              employees.map(employee => (
                <MenuItem
                  key={employee.id}
                  onClick={() => handleClick(employee)}
                  value={employee.id}
                  sx={{ color: 'text.base' }}
                >
                  {`${employee.firstName} ${employee.lastName}`}
                </MenuItem>
              ))
            ) : (
              <MenuItem
                sx={{
                  color: 'text.base',
                  '&.Mui-disabled': {
                    opacity: 1,
                  },
                }}
                disabled={true}
              >
                {t('page.projectDistribution.noLeaderEmployees')}
              </MenuItem>
            )}
          </Menu>
        </React.Fragment>
      )}
    </PopupState>
  )
}

export default AddEmployeeMenuPopup